import React, { useContext, createContext, useCallback, useMemo, useState, useEffect, useLayoutEffect, useReducer } from "react";

import KeyValueFilter from "./KeyValueFilter";
import { v4 as uuid } from "uuid";
import { ToolbarContext } from "@acadia-ui/toolbar";
import { shouldFilter } from "../utils";

export const KeyValueFiltersContext = createContext();

export default function useKeyValueFilters({ isOpen, prefix }) {
	const context = useContext(ToolbarContext);
	const [value, setValue] = useState({});
	const [elements, setElements] = useState([]);

	useEffect(() => {
		if (isOpen) {
			if (shouldFilter(`${prefix}-filter-keyValueFilter`, context?.filterValues)) {
				const identifiedValues = Object.fromEntries(
					Object.entries(context?.filterValues?.[`${prefix}-filter-keyValueFilter`])?.map?.(([dataKey, values]) => {
						return [dataKey, { values, id: uuid() }];
					})
				);

				const newElements = Object.values(identifiedValues)?.map(({ id }, index, arr) => (
					<KeyValueFilter key={id} uuid={id} position={index} totalNumFilters={arr.length} />
				));

				setValue(identifiedValues);
				setElements(newElements);
			}
		} else {
			setValue({});
			setElements([]);
		}
		// Intentionally exclude context from deps array as we only want to rerender when isOpen changes to true
	}, [isOpen, prefix]);

	const handleDeleteDataKey = useCallback((position) => {
		setElements((prevElements) => {
			return (
				prevElements
					?.filter((_, index) => index !== position)
					?.map((element, index) =>
						React.cloneElement(element, {
							position: index,
							totalNumFilters: prevElements.length - 1
						})
					) || []
			);
		});
	}, []);

	const handleAddDataKey = useCallback(() => {
		const uuidValue = uuid();
		setElements((prevElements) => [
			...(prevElements?.map((element, index) => {
				return React.cloneElement(element, {
					position: index,
					totalNumFilters: prevElements.length + 1
				});
			}) || []),
			<KeyValueFilter key={uuidValue} uuid={uuidValue} position={prevElements.length} totalNumFilters={prevElements.length + 1} />
		]);
	}, []);

	useLayoutEffect(() => {
		if (elements?.length === 0) {
			const uuidValue = uuid();
			setElements([<KeyValueFilter key={uuidValue} uuid={uuidValue} position={0} totalNumFilters={1} />]);
		}
	}, [elements]);

	const contextValue = useMemo(
		() => ({
			value,
			setValue,
			handleDelete: handleDeleteDataKey,
			handleAdd: handleAddDataKey,
			isOpen
		}),
		[handleAddDataKey, handleDeleteDataKey, value, isOpen]
	);

	const formattedValue = useMemo(() => {
		// Format the value, removing empty values arrays and id property
		return Object.fromEntries(
			Object.entries(value)
				.map(([key, value]) => {
					if (value.values.length > 0) {
						return [key, value.values];
					}
					return null;
				})
				?.filter((value) => value !== null) || []
		);
	}, [value]);

	const clearValues = useCallback(() => {
		setValue({});
		setElements([]);
	}, [setValue]);

	return useMemo(
		() => ({
			// This comment exists to prevent eslint from making this unreadable.
			element: <KeyValueFiltersContext.Provider value={contextValue}>{elements}</KeyValueFiltersContext.Provider>,
			value: formattedValue,
			clearValues
		}),
		[elements, formattedValue, contextValue, clearValues]
	);
}
